import type { SynthId } from '@/types'

import { renderPlaceholder } from '@/lib/audio/synth'

// One audio engine for the whole page: track previews and instrument samples share it,
// so only one stream ever plays at a time. Menu cues are separate short blips.

export type PlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error' | 'blocked'

export type PlaybackSnapshot = {
    activeId: string | null
    status: PlaybackStatus
    position: number
    duration: number
}

export type PlayableSource = {
    id: string
    url: string | null
    synth: SynthId
    durationHint: number
}

export type CueKind = 'move' | 'confirm' | 'back'

type Listener = () => void

type WebkitWindow = Window & {
    webkitAudioContext?: typeof AudioContext
}

type SessionNavigator = Navigator & {
    audioSession?: { type: string }
}

const IDLE: PlaybackSnapshot = {
    activeId: null,
    status: 'idle',
    position: 0,
    duration: 0
}

const decode = (ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> => {
    return new Promise((resolve, reject) => {
        ctx.decodeAudioData(data, resolve, reject)
    })
}

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms))

class AudioEngine {
    private ctx: AudioContext | null = null
    private master: GainNode | null = null
    private analyser: AnalyserNode | null = null
    private noise: AudioBuffer | null = null
    private pending = new Map<string, Promise<AudioBuffer>>()
    private ready = new Map<string, AudioBuffer>()
    private node: AudioBufferSourceNode | null = null
    private nodeStartedAt = 0
    private nodeOffset = 0
    private nodeDuration = 0
    private current: PlayableSource | null = null
    private snapshot: PlaybackSnapshot = IDLE
    private listeners = new Set<Listener>()
    private ticket = 0
    private frame = 0
    private lastEmit = 0

    sfx = false

    subscribe = (listener: Listener): (() => void) => {
        this.listeners.add(listener)

        return () => {
            this.listeners.delete(listener)
        }
    }

    getSnapshot = (): PlaybackSnapshot => this.snapshot

    getServerSnapshot = (): PlaybackSnapshot => IDLE

    private emit(next: Partial<PlaybackSnapshot>): void {
        this.snapshot = { ...this.snapshot, ...next }
        this.listeners.forEach(listener => listener())
    }

    private ensureContext(): AudioContext | null {
        if (this.ctx) return this.ctx
        if (typeof window === 'undefined') return null

        const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext
        if (!Ctor) return null

        try {
            const nav = navigator as SessionNavigator
            if (nav.audioSession) nav.audioSession.type = 'playback'
        }
        catch {
            // Older browsers have no audio session control; nothing to do.
        }

        const ctx = new Ctor()
        const master = ctx.createGain()
        const analyser = ctx.createAnalyser()

        master.gain.value = 0.9
        analyser.fftSize = 64
        analyser.smoothingTimeConstant = 0.72

        master.connect(analyser)
        analyser.connect(ctx.destination)

        this.ctx = ctx
        this.master = master
        this.analyser = analyser

        return ctx
    }

    private keyFor(source: PlayableSource): string {
        return source.url ?? source.synth
    }

    private load(source: PlayableSource): Promise<AudioBuffer> {
        const key = this.keyFor(source)
        const existing = this.pending.get(key)
        if (existing) return existing

        const ctx = this.ctx
        const promise = (async () => {
            if (source.url) {
                if (!ctx) throw new Error('Audio is not available')

                const controller = new AbortController()
                const timer = window.setTimeout(() => controller.abort(), 10000)

                try {
                    const response = await fetch(source.url, { signal: controller.signal })
                    if (!response.ok) throw new Error('Audio request failed')

                    return await decode(ctx, await response.arrayBuffer())
                }
                finally {
                    window.clearTimeout(timer)
                }
            }

            return renderPlaceholder(source.synth)
        })()

        this.pending.set(key, promise)

        promise
            .then(buffer => {
                this.ready.set(key, buffer)
            })
            .catch(() => {
                this.pending.delete(key)
            })

        return promise
    }

    // Warm a placeholder in the background so the first tap feels instant.
    prepare(source: PlayableSource): void {
        if (source.url) return

        this.load(source).catch(() => {
            // A failed warm-up is retried on the real tap.
        })
    }

    private stopNode(): void {
        const node = this.node
        this.node = null
        this.stopTicker()

        if (!node) return

        node.onended = null

        try {
            node.stop()
        }
        catch {
            // Already stopped.
        }

        node.disconnect()
    }

    private position(): number {
        if (!this.node || !this.ctx) return this.snapshot.position

        const elapsed = this.ctx.currentTime - this.nodeStartedAt
        return Math.min(this.nodeDuration, this.nodeOffset + elapsed)
    }

    private startNode(buffer: AudioBuffer, offset: number): void {
        const ctx = this.ctx
        const master = this.master
        if (!ctx || !master) return

        const node = ctx.createBufferSource()
        const safeOffset = Math.max(0, Math.min(offset, buffer.duration - 0.05))

        node.buffer = buffer
        node.connect(master)
        node.onended = () => {
            if (this.node !== node) return

            this.node = null
            this.stopTicker()
            this.current = null
            this.emit({ activeId: null, status: 'idle', position: 0 })
        }

        node.start(0, safeOffset)

        this.node = node
        this.nodeStartedAt = ctx.currentTime
        this.nodeOffset = safeOffset
        this.nodeDuration = buffer.duration

        this.emit({
            activeId: this.current?.id ?? null,
            status: 'playing',
            position: safeOffset,
            duration: buffer.duration
        })
        this.startTicker()
    }

    private startTicker(): void {
        this.stopTicker()

        const tick = (now: number) => {
            if (!this.node) return

            if (now - this.lastEmit > 50) {
                this.lastEmit = now
                this.emit({ position: this.position() })
            }

            this.frame = window.requestAnimationFrame(tick)
        }

        this.frame = window.requestAnimationFrame(tick)
    }

    private stopTicker(): void {
        if (this.frame) window.cancelAnimationFrame(this.frame)
        this.frame = 0
    }

    async play(source: PlayableSource, from = 0): Promise<void> {
        const ctx = this.ensureContext()
        const ticket = ++this.ticket

        this.stopNode()
        this.current = source

        if (!ctx) {
            this.emit({ activeId: source.id, status: 'error', position: 0, duration: source.durationHint })
            return
        }

        const cached = this.ready.get(this.keyFor(source))

        // resume() has to be called inside the tap itself for iOS and Chrome to allow sound.
        const resuming = ctx.state === 'running' ? Promise.resolve() : ctx.resume()

        if (cached && ctx.state === 'running') {
            this.startNode(cached, from)
            return
        }

        this.emit({
            activeId: source.id,
            status: 'loading',
            position: from,
            duration: cached?.duration ?? source.durationHint
        })

        try {
            await Promise.race([resuming, wait(1500)])
        }
        catch {
            // Handled by the state check below.
        }

        if (ticket !== this.ticket) return

        if (ctx.state !== 'running') {
            this.emit({ status: 'blocked' })
            return
        }

        let buffer: AudioBuffer

        try {
            buffer = cached ?? await this.load(source)
        }
        catch {
            if (ticket === this.ticket) this.emit({ status: 'error' })
            return
        }

        if (ticket !== this.ticket) return

        this.startNode(buffer, from)
    }

    pause(): void {
        if (this.snapshot.status !== 'playing') return

        const position = this.position()
        this.ticket++
        this.stopNode()
        this.emit({ status: 'paused', position })
    }

    stop(): void {
        this.ticket++
        this.stopNode()
        this.current = null
        this.emit(IDLE)
    }

    toggle(source: PlayableSource): void {
        const { activeId, status, position } = this.snapshot

        if (activeId !== source.id) {
            void this.play(source, 0)
            return
        }

        if (status === 'playing') {
            this.pause()
            return
        }

        if (status === 'loading') {
            this.stop()
            return
        }

        if (status === 'paused' || status === 'blocked') {
            void this.play(source, position)
            return
        }

        void this.play(source, 0)
    }

    seek(source: PlayableSource, seconds: number): void {
        const { activeId, status } = this.snapshot
        const target = Math.max(0, seconds)

        if (activeId === source.id && status === 'paused') {
            this.emit({ position: Math.min(target, this.snapshot.duration) })
            return
        }

        void this.play(source, target)
    }

    readLevels(out: Uint8Array<ArrayBuffer>): boolean {
        if (!this.analyser || !this.node) {
            out.fill(0)
            return false
        }

        this.analyser.getByteFrequencyData(out)
        return true
    }

    isActive(id: string): boolean {
        return this.current?.id === id
    }

    cue(kind: CueKind): void {
        if (!this.sfx) return

        const ctx = this.ensureContext()
        if (!ctx) return

        if (ctx.state !== 'running') {
            ctx.resume().catch(() => {
                // Sound is not allowed yet; the cue is simply skipped.
            })
            return
        }

        const t = ctx.currentTime + 0.005
        const gain = ctx.createGain()
        gain.connect(ctx.destination)

        if (kind === 'back') {
            if (!this.noise) {
                this.noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.2), ctx.sampleRate)
                const data = this.noise.getChannelData(0)
                for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
            }

            const source = ctx.createBufferSource()
            const band = ctx.createBiquadFilter()

            source.buffer = this.noise
            band.type = 'bandpass'
            band.frequency.setValueAtTime(2400, t)
            band.frequency.exponentialRampToValueAtTime(700, t + 0.06)
            band.Q.value = 1.4
            gain.gain.setValueAtTime(0.0001, t)
            gain.gain.exponentialRampToValueAtTime(0.09, t + 0.004)
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.07)

            source.connect(band).connect(gain)
            source.start(t)
            source.stop(t + 0.08)
            return
        }

        const osc = ctx.createOscillator()
        osc.type = 'square'

        if (kind === 'move') {
            osc.frequency.setValueAtTime(1046, t)
            osc.frequency.exponentialRampToValueAtTime(784, t + 0.04)
            gain.gain.setValueAtTime(0.0001, t)
            gain.gain.exponentialRampToValueAtTime(0.03, t + 0.003)
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.045)
            osc.start(t)
            osc.stop(t + 0.05)
        }
        else {
            osc.frequency.setValueAtTime(784, t)
            osc.frequency.setValueAtTime(1568, t + 0.035)
            gain.gain.setValueAtTime(0.0001, t)
            gain.gain.exponentialRampToValueAtTime(0.035, t + 0.003)
            gain.gain.setValueAtTime(0.035, t + 0.06)
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08)
            osc.start(t)
            osc.stop(t + 0.085)
        }

        osc.connect(gain)
    }
}

export const audioEngine = new AudioEngine()
